import type { AppRegistration, AppRegistrationVersion } from '@ms-cloudpack/remote-cache';
import type { CommandExitParams } from '../types/CommandAction.js';
import { getCachePath } from '@ms-cloudpack/config';
import { selectItemFromPaginatedResults } from './selectItemFromPaginatedResults.js';
import type { SharedOptions } from '../types/SharedOptions.js';
import type { ReusedOptions } from '../types/ReusedOptions.js';
import { createNoOpTelemetryClient } from '@ms-cloudpack/telemetry';
import type { Context } from '@ms-cloudpack/api-server';
import path from 'path';

export async function discoverRemoteAppPaths(params: {
  context: Pick<Context, 'reporter'>;
  options: {
    /**
     * Desired app name or substring, from the `--app` option.
     * - If there's an exact match, only that app will be returned.
     * - If multiple apps match a substring, see `discover` for behavior.
     * - If no match, it's an error.
     */
    desiredApp?: string;
  } & Pick<SharedOptions, 'cachePath'> &
    Pick<ReusedOptions, 'login'>;
}): Promise<string[] | CommandExitParams> {
  const { options } = params;
  const { desiredApp, login } = options;

  const { createRemoteCacheClient, createAppRegistryClient } = await import('@ms-cloudpack/remote-cache');

  const cachePath = await getCachePath(options.cachePath);

  const context = {
    ...params.context,
    telemetryClient: createNoOpTelemetryClient(),
  };

  const client = await createAppRegistryClient({
    options: {
      cachePath,
      loginMethod: login,
    },
    context,
  });

  const app = await selectItemFromPaginatedResults<AppRegistration>({
    message: 'Select the app you want to start',
    getPage: (pageSettings) => client.getApps(desiredApp, pageSettings),
    getTitle: (item) => item.name,
  });

  if (!app) {
    return { isInterrupted: true, message: '' };
  }

  const version = await selectItemFromPaginatedResults<AppRegistrationVersion>({
    message: `Select the version of ${app.name} you want to start`,
    getPage: (pageSettings) => client.getVersions(app.name, pageSettings),
    getTitle: (item) => item.version,
  });

  if (!version) {
    return { isInterrupted: true, message: '' };
  }

  const remoteCacheClient = await createRemoteCacheClient({
    context,
    options: {
      cachePath,
      loginMethod: login,
      storageAccount: version.storageAccount,
      tenantId: version.tenantId,
      container: version.container,
    },
  });

  const targetPath = path.join(cachePath, version.localBundleFolderName);

  const downloadStatus = await remoteCacheClient.downloadFolder({
    friendlyName: `${app.name}-${version.version}`,
    id: version.resourceId,
    path: targetPath,
  });

  if (downloadStatus === 'not-found') {
    return { message: 'The specified resource was not found in the remote cache.', hasErrors: true };
  }

  return [path.join(targetPath, '__cloudpack')];
}
